import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { createMerchants } from "../../common/makeAPICalls";
import CustomerForm, { type CustomerData } from "./CustomerForm";
import DetailsEnterprise, {
  type DetailsEnterpriseData,
} from "./DetailsEnterprise";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface CreateMerchantFormProps {
  merchantObject: DetailsEnterpriseData;
  representative: CustomerData;
}
const style = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  borderRadius: "10px",
  overflow: "hidden",
};

export default function CreateMerchantForm({
  onClose,
}: {
  onClose: () => void;
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const [disableButton, setDisableButton] = useState(false);
  const { t } = useTranslation();

  const steps = [t("createMerchant.step1"), t("createMerchant.step2")];
  const enterpriseRef = useRef<{
    getEnterPriseData: () => DetailsEnterpriseData;
  }>(null);
  const representRef = useRef<{ getFormattedData: () => CustomerData }>(null);
  const isLast = activeStep === steps.length - 1;

  const createNewMerchants = async (payload: CreateMerchantFormProps) => {
    try {
      const responseData = await createMerchants(payload);
      if ("id" in responseData && responseData.id) {
        toast.success(t("createMerchant.successMessage"));
        setTimeout(() => {
          setLoading(false);
          onClose();
        }, 2000);
      } else {
        const details = "details" in responseData ? responseData.details : "";
        toast.error(t("createMerchant.errorMessage") + details);
        setLoading(false);
      }
    } catch (error) {
      console.error(t("createMerchant.errorMessage"), error);
      toast.error(t("createMerchant.errorMessage") + error);
      setLoading(false);
      throw error;
    }
  };

  const handleNext = async () => {
    if (!isLast) {
      return setActiveStep((s) => s + 1);
    }
    setLoading(true);
    try {
      const merchantObjectData = enterpriseRef.current?.getEnterPriseData();
      const representativeData = representRef.current?.getFormattedData();
      if (!merchantObjectData) {
        console.error("Merchant object data is undefined");
        toast.error("Merchant object data is undefined");
        setLoading(false);
        return;
      }

      const payload = {
        merchantObject: merchantObjectData,
        representative: representativeData,
      };
      // API call with `payload`
      createNewMerchants(payload as CreateMerchantFormProps);
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      setActiveStep(0);
      setLoading(false);
    };
  }, []);

  return (
    <Box
      component="form"
      sx={{
        ...style,
        width: "90vw",
        maxWidth: 600,
        "& .MuiTextField-root": { m: 0 },
      }}
      noValidate
      autoComplete="off"
    >
      {/* Header fixe */}
      <Box sx={{ px: { xs: 3, sm: 5 }, pt: { xs: 2, sm: 2.5 }, flexShrink: 0 }}>
        <Box mb={1.5} display="flex" alignItems="center">
          <Typography flexGrow={1} fontWeight={700} fontSize={18}>
            {t("createMerchant.title")}
          </Typography>
          <IconButton onClick={onClose} aria-label={t("button.cancel")}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Stepper activeStep={activeStep} sx={{ width: "100%" }}>
          {steps.map((label) => (
            <Step
              key={label}
              sx={{
                "& .MuiStepLabel-root .Mui-completed": {
                  color: "#20A144",
                },
                "& .MuiStepLabel-label.Mui-completed": {
                  color: "grey.500",
                },
                "& .MuiStepLabel-root .Mui-active": {
                  color: (t) => t.palette.primary.main,
                },
                "& .MuiStepLabel-label.Mui-active": {
                  padding: "10px 0",
                  color: (t) => t.palette.primary.main,
                },
                "& .MuiStepLabel-root .Mui-active .MuiStepIcon-text": {
                  fill: "white",
                },
                "& .MuiStepLabel-horizontal": {
                  display: "flex",
                  flexDirection: "column",
                },
                "& .MuiStepLabel-label": {
                  padding: "10px 0",
                },
              }}
            >
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Corps scrollable */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: { xs: 3, sm: 5 },
          pt: 0,
          pb: 1,
          // Scrollbar stylée
          scrollbarWidth: "thin",
          scrollbarColor: "#D9D9D9 transparent",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#D9D9D9",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#BDBDBD",
          },
        }}
      >
        <Box sx={{ display: activeStep === 0 ? "block" : "none" }}>
          <DetailsEnterprise
            ref={enterpriseRef}
            onValidChange={(valid: boolean) =>
              activeStep === 0 && setDisableButton(valid)
            }
          />
        </Box>
        <Box sx={{ display: activeStep === 1 ? "block" : "none" }}>
          <CustomerForm
            ref={representRef}
            onValidChange={(valid: boolean) =>
              activeStep === 1 && setDisableButton(valid)
            }
          />
        </Box>
      </Box>

      {/* Footer fixe */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          px: { xs: 3, sm: 5 },
          py: 2,
          borderTop: "1px solid #F0F0F0",
          flexShrink: 0,
        }}
      >
        <Button
          onClick={() => {
            setActiveStep(0);
            onClose();
          }}
          color="inherit"
          sx={{
            mr: 1,
            textTransform: "none",
            padding: "8px 15px",
            borderRadius: "30px",
            backgroundColor: "#979797",
            fontWeight: "bold",
            color: "#fff",
          }}
        >
          {t("button.cancel")}
        </Button>
        <Button
          onClick={handleNext}
          loading={loading}
          loadingPosition="start"
          startIcon={<SaveIcon />}
          variant="contained"
          disabled={disableButton}
          sx={{
            ".MuiButtonBase-root:disabled": {
              cursor: "not-allowed",
              pointerEvents: "auto",
              backgroundColor: "#979797",
            },
            ".MuiButtonBase-root:hover": {
              backgroundColor: (t) => t.palette.primary.main,
            },
            textTransform: "none",
            padding: "8px 14px",
            borderRadius: "30px",
            backgroundColor: (t) => t.palette.primary.main,
            fontWeight: "bold",
            color: "#fff",
          }}
        >
          {activeStep === 0
            ? t("button.next")
            : t("createMerchant.submitButton")}
        </Button>
      </Box>
    </Box>
  );
}
